import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePagination, PaginationNav, PAGE_SIZE, SearchBar, matchesSearch, ConfirmDialog } from "@/components/tableUtils";

const EMPTY = { nama: "", kepala_ruangan: "", nip: "" };

export default function Ruangan() {
  const [rooms, setRooms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [confirmRoom, setConfirmRoom] = useState(null);
  const filtered = rooms.filter((r) => matchesSearch(r, ["nama", "kepala_ruangan", "nip"], search));
  const { page, setPage, pageCount } = usePagination(filtered.length);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const load = () => api.get("/rooms").then((r) => { setRooms(r.data); setPage(1); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ nama: r.nama, kepala_ruangan: r.kepala_ruangan, nip: r.nip });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.nama.trim()) {
      toast.error("Nama ruangan wajib diisi");
      return;
    }
    try {
      if (editing) await api.put(`/rooms/${editing.id}`, form);
      else await api.post("/rooms", form);
      toast.success(editing ? "Ruangan diperbarui" : "Ruangan ditambahkan");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/rooms/${r.id}`);
      toast.success(`Ruangan ${r.nama} dihapus`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="ruangan-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Data Ruangan</h1>
          <p className="text-sm text-muted-foreground">Kelola nama ruangan beserta kepala ruangan. Terhubung ke jadwal maintenance.</p>
        </div>
        <Button onClick={openAdd} data-testid="tambah-ruangan-button">
          <Plus className="w-4 h-4 mr-2" /> Tambah Ruangan
        </Button>
      </div>

      <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} testid="ruangan-search" placeholder="Cari nama ruangan atau kepala ruangan..." />

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama Ruangan</TableHead>
              <TableHead>Kepala Ruangan</TableHead>
              <TableHead>NIP Kepala Ruangan</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((r) => (
              <TableRow key={r.id} data-testid={`room-row-${r.id.slice(0, 8)}`}>
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary" /> {r.nama}
                  </span>
                </TableCell>
                <TableCell>{r.kepala_ruangan || "—"}</TableCell>
                <TableCell>{r.nip || "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)} title="Edit" data-testid={`edit-room-${r.id.slice(0, 8)}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setConfirmRoom(r)} title="Hapus" data-testid={`delete-room-${r.id.slice(0, 8)}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paged.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-10" data-testid="rooms-empty">
                  Belum ada ruangan. Klik "Tambah Ruangan" untuk memulai.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <PaginationNav page={page} pageCount={pageCount} onChange={setPage} testid="rooms-pagination" />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="room-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Ruangan" : "Tambah Ruangan"}</DialogTitle>
            <DialogDescription>Lengkapi data ruangan dan kepala ruangan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nama Ruangan</Label>
              <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Contoh: IGD" data-testid="room-form-nama" />
            </div>
            <div className="space-y-2">
              <Label>Kepala Ruangan</Label>
              <Input value={form.kepala_ruangan} onChange={(e) => setForm({ ...form, kepala_ruangan: e.target.value })} data-testid="room-form-kepala" />
            </div>
            <div className="space-y-2">
              <Label>NIP Kepala Ruangan</Label>
              <Input value={form.nip} onChange={(e) => setForm({ ...form, nip: e.target.value })} data-testid="room-form-nip" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="room-form-cancel">Batal</Button>
            <Button onClick={save} data-testid="room-form-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmRoom}
        onOpenChange={(o) => !o && setConfirmRoom(null)}
        onConfirm={() => { remove(confirmRoom); setConfirmRoom(null); }}
        title="Apakah Anda yakin?"
        description={`Ruangan "${confirmRoom?.nama}" akan dihapus permanen.`}
        confirmLabel="Ya, Hapus"
        testid="confirm-delete-room"
      />
    </div>
  );
}
